import moment from 'moment';
import momentTZ from 'moment-timezone';
import pluralize from 'pluralize';
import { Experience } from 'interface/experience/experience.interface';
import { FullTime } from 'types/common';

export const coerceEpoch = (epochOrDate: number | Date) => {
  if (typeof epochOrDate === 'number') return epochOrDate;
  return Math.floor(epochOrDate.getTime() / 1000);
};

export const coerceDate = (epochOrDate: number | Date) => {
  if (epochOrDate instanceof Date) return epochOrDate;
  return new Date(epochOrDate * 1000);
};

/**
 * Converts an epoch timestamp or a Date object to a localized date-time string.
 *
 * @param epochOrDate - The epoch timestamp (in milliseconds) or a Date object to be converted.
 * @param locale - The locale string used for formatting the date-time string. Defaults to 'en-US'.
 * @param options - An object specifying options for the `Intl.DateTimeFormat` API. Defaults to `{ month: 'short', day: 'numeric' }`.
 * @returns A localized date-time string based on the provided epoch or Date object.
 */
export const epochToDateTimeString = (
  epochOrDate: number | Date,
  locale = 'en-US',
  options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
  },
) => {
  const epochValue = typeof epochOrDate === 'number' ? epochOrDate : epochOrDate.getTime();
  return new Date(epochValue).toLocaleString(locale, options);
};

/**
 * Calculates the relative time difference between the given date or timestamp and the current time,
 * and returns a human-readable string representing the time difference (e.g., "2 days ago").
 */
export const timeAgo = (input: Date | number) => {
  const date = input instanceof Date ? input : new Date(input);
  const formatter = new Intl.RelativeTimeFormat('en');
  const ranges = [
    ['years', 3600 * 24 * 365],
    ['months', 3600 * 24 * 30],
    ['weeks', 3600 * 24 * 7],
    ['days', 3600 * 24],
    ['hours', 3600],
    ['minutes', 60],
    ['seconds', 1],
  ] as const;
  const secondsElapsed = (date.getTime() - Date.now()) / 1000;

  for (const [rangeType, rangeVal] of ranges) {
    if (rangeVal < Math.abs(secondsElapsed)) {
      const delta = secondsElapsed / rangeVal;
      return formatter.format(Math.round(delta), rangeType);
    }
  }
  return formatter.format(-1, 'seconds');
};

// moment is depreciated, but the timezones are a hassle to deal with so uh yeah
export const convertEpochToTime = (timestamp?: number | 'NOW', timezone?: string, omit?: boolean): FullTime => {
  if (!timestamp) return { isActive: omit !== undefined ? !omit : false };

  const date = timestamp === 'NOW' ? moment() : moment.unix(timestamp);
  if (timezone) {
    // we want to convert the timezone to a format moment can understand, like "America/New_York"
    const formattedTimezone = timezone.substring(0, timezone.indexOf('(')).trim().replace(' ', '_');
    date.tz(formattedTimezone);
  }
  const seconds = moment.duration(date.format('HH:mm')).asSeconds();
  // Read the Y-M-D as it appears in the selected timezone, then return a Date
  // pinned to UTC midnight of that same Y-M-D. The <input type="date"> picker
  // reads the Date via getISODate, which formats through UTC ISO — so having
  // the Date's UTC calendar date equal the tz-local calendar date keeps the
  // picker stable across timezone changes.
  const tzLocalYMD = date.format('YYYY-MM-DD');
  return {
    isActive: omit !== undefined ? !omit : true,
    // moment.utc(str, 'YYYY-MM-DD') parses as UTC midnight of that date.
    date: moment.utc(tzLocalYMD, 'YYYY-MM-DD').toDate(),
    time: {
      key: seconds,
      name: date.format('hh:mm A'),
      value: seconds,
    },
    shortDateString: date.format('ddd, MMM D, YYYY'),
  };
};

export function getISODate(date?: Date) {
  if (!date || (!(date instanceof Date) && isNaN(date as unknown as number))) return '';
  if (date.toString() == 'Invalid Date') return '';
  return (date as Date).toISOString().split('T')[0] || '';
}

/**
 * Unix timestamp to ISO String with an offset.
 * Offset should be given in milliseconds.
 */
export function unixToOffsetISO(timestamp: number, offset = 0) {
  return new Date(timestamp * 1000 - offset).toISOString().slice(0, -8);
}

/**
 * ISO String to Unix timestamp with an offset.
 * Offset should be given in milliseconds.
 */
export function offsetISOToUnix(isoDateTime: string, offset = 0) {
  return (new Date(`${isoDateTime}Z`).getTime() + offset) / 1000;
}

/**
 * @returns current timestamp in seconds
 */
export function getCurrentTimestampInMs() {
  return Math.floor(new Date().getTime() / 1000);
}

/**
 * Returns the list of timezones from the 'moment' library
 */
export function getTimezoneList(searchZone = '') {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let timezones = momentTZ.tz.names().map((timezone: any, idx: number) => {
    return {
      key: idx + 1,
      name: timezone.replace(/_/g, ' ') + ' (UTC ' + momentTZ.tz(timezone).format('Z') + ')',
      offset: parseInt(momentTZ.tz(timezone).format('Z')),
    };
  });

  if (searchZone) {
    timezones = timezones.filter((timezone) => {
      return timezone.name.toLowerCase().search(searchZone.toLowerCase().replace(/[_)(+]/g, ' ')) >= 0;
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return timezones.sort((a: any, b: any) => (a.name > b.name ? 1 : b.name > a.name ? -1 : 0));
}

/**
 * Returns an estimate of the user's timezone from the 'moment' library
 */
export function guessUserTimezone() {
  const timezone = moment.tz.guess();
  return timezone.replace(/_/g, ' ') + ' (UTC ' + momentTZ.tz(timezone).format('Z') + ')';
}

/**
 * Calculate time ranges give a min/max in epoch, a selected date, and a
 * timezone-based offset in hours. Returns min max in minutes past midnight.
 */
export function getTimeRangeInMinutes(date?: Date, min?: number, max?: number, interval = 15) {
  const retVal: { min?: number; max?: number } = { min: undefined, max: undefined };

  if (!date) return retVal;

  const secondsInDay = 60 * 60 * 24;
  const dateEpoch = date.getTime() / 1000;

  if (min && dateEpoch < min) {
    retVal.min = Math.ceil((min - dateEpoch) / 60.0 / interval) * interval;
  }

  if (max && dateEpoch + secondsInDay > max) {
    retVal.max = Math.ceil((max - dateEpoch) / 60.0 / interval) * interval;
  }

  return retVal;
}

/**
 * Returns timezone name without UTC data
 */
export function cleanupTimezone(timezone: string) {
  const start = timezone?.indexOf(' (UTC');
  if (start > -1) {
    timezone = timezone?.slice(0, start);
  }
  timezone = timezone?.replace(/ /, '_');
  return timezone;
}

/**
 * Builds a unix timestamp by interpreting a wall-clock (calendar date + seconds
 * past midnight) in the given display-form timezone name (e.g.
 * "America/Toronto (UTC -04:00)"). Returns 0 when inputs are missing.
 *
 * This is the inverse of `convertEpochToTime`: switching the timezone input
 * without changing `date`/`secondsOfDay` yields a different unix, while keeping
 * the displayed wall-clock stable.
 */
export function buildUnixInTz(date?: Date, secondsOfDay?: number, timezoneName?: string): number {
  if (!date || secondsOfDay === undefined) return 0;
  const dateStr = moment.utc(date).format('YYYY-MM-DD');
  const timeStr = moment.utc(secondsOfDay * 1000).format('HH:mm');
  const cleanTz = timezoneName ? cleanupTimezone(timezoneName) : undefined;
  return cleanTz
    ? momentTZ.tz(`${dateStr} ${timeStr}`, 'YYYY-MM-DD HH:mm', cleanTz).unix()
    : moment(`${dateStr} ${timeStr}`, 'YYYY-MM-DD HH:mm').unix();
}

/**
 * Returns date and time, converted to timezone + user's local time
 */
export function convertDateToTimezone(date: number, timezone: string) {
  const dateFormatted = moment(date * 1000).format('YYYY-MM-DD HH:mm');
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const selZoneTime = momentTZ.tz(dateFormatted, cleanupTimezone(timezone!));
  const localZoneTime = selZoneTime.clone().tz(moment.tz.guess());
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const dateTZFormatted = momentTZ.tz(date * 1000, cleanupTimezone(timezone!)).format('YYYY-MM-DD HH:mm');
  const localZoneUnixTime = moment(dateTZFormatted).unix();
  return {
    tz_date: selZoneTime.format('MMM D, YYYY'),
    tz_time: selZoneTime.format('hh:mmA'),
    tz_unix: localZoneUnixTime,
    local_date: localZoneTime.format('MMM D, YYYY'),
    local_time: localZoneTime.format('hh:mmA'),
    local_unix: localZoneTime.unix(),
  };
}

/**
 * Returns date and time, converted to timezone + user's local time
 */
export function convertTimezoneToDate(date: number, timezone: string) {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const dateFormatted = moment(date! * 1000).format('YYYY-MM-DD HH:mm:ss');
  const localZoneTime = momentTZ.tz(dateFormatted, moment.tz.guess());
  const selZoneTime = timezone ? localZoneTime.clone().tz(cleanupTimezone(timezone)) : null;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const dateTZFormatted = momentTZ.tz(date! * 1000, cleanupTimezone(timezone)).format('YYYY-MM-DD HH:mm:ss');
  const localZoneUnixTime = moment(dateTZFormatted).unix();

  return {
    tz_date: selZoneTime ? selZoneTime.format('MMM D, YYYY') : '',
    tz_time: selZoneTime ? selZoneTime.format('hh:mmA') : '',
    tz_unix: localZoneUnixTime,
    local_date: localZoneTime.format('MMM D, YYYY'),
    local_time: localZoneTime.format('hh:mmA'),
    local_unix: localZoneTime.unix(),
  };
}

const PERIODS = ['months', 'weeks', 'days', 'hours', 'minutes', 'seconds'] as const;
const PeriodValues = {
  months: 2592000,
  weeks: 604800,
  days: 86400,
  hours: 3600,
  minutes: 60,
  seconds: 1,
};
type Period = (typeof PERIODS)[number];

/**
 * Returns the string representation of the difference between two epochs.
 */
export function getDuration(
  start: number,
  end: number,
  maxPeriod: Period = 'months',
  minPeriod: Period = 'minutes',
  shownPeriodCount = 2,
): string {
  if (end === -1) return 'No End';
  const duration = end - start;
  if (duration < 0) return 'Invalid Duration';

  const maxIdx = PERIODS.indexOf(maxPeriod);
  const minIdx = PERIODS.indexOf(minPeriod);
  const shownPeriods: { value: number; period: Period; text: string }[] = [];

  for (let i = maxIdx; i <= minIdx; i++) {
    if (shownPeriods.length === shownPeriodCount) break;
    const remainingTime = shownPeriods.reduce((acc, curr) => acc - curr.value * PeriodValues[curr.period], duration);
    const value = Math.floor(remainingTime / PeriodValues[PERIODS[i]]);

    if (value > 0 || shownPeriods.length || (shownPeriods.length === 0 && i === minIdx))
      shownPeriods.push({
        value,
        period: PERIODS[i],
        text: `${value} ${value === 1 ? PERIODS[i].slice(0, -1) : PERIODS[i]}`,
      });
    else continue;
  }

  for (let i = shownPeriods.length - 1; i > 0; i--) {
    if (shownPeriods[i].value) break;
    shownPeriods.pop();
  }

  return shownPeriods.map((p) => p.text).join(', ');
}

/**
 * Returns the object with warning variables on launching scheduled experience
 */
export function getWarningOnLaunchScheduled(schedule?: Experience['schedule']) {
  const startDate = moment((schedule?.start || 0) * 1000)?.format('MMM DD, YYYY [at] h:mmA');
  const endDate = schedule?.end ? moment((schedule.end || 0) * 1000)?.format('MMM DD, YYYY [at] h:mmA') : 'No end date';
  let newTitle = '';
  let newMessage = '';
  if (schedule?.start) {
    newTitle = 'Launch on date';
    newMessage =
      'This experience has a schedule associated with it. As a result, it will launch with the following dates:';
  }
  return { startDate, endDate, newTitle, newMessage };
}

/**
 * Returns number of seconds in period
 */
export function convertPeriodToSeconds(value: string | number, period: string) {
  const periodValue = typeof value === 'string' ? parseInt(value) : value;
  let time = periodValue || 0;

  if (!period) return time;
  period = period === 'now' ? period : pluralize(period);

  const secondsInMinute = moment.duration(1, 'minutes').asSeconds();
  const secondsInHour = moment.duration(1, 'hours').asSeconds();
  const secondsInDay = moment.duration(1, 'days').asSeconds();
  const secondsInWeek = moment.duration(1, 'weeks').asSeconds();
  const secondsInMonth = moment.duration(1, 'months').asSeconds();
  const secondsInYear = moment.duration(1, 'years').asSeconds();

  if (period === 'now') {
    time = 0;
  }
  if (period === 'minutes') {
    time = periodValue * secondsInMinute;
  }
  if (period === 'hours') {
    time = periodValue * secondsInHour;
  }
  if (period === 'days') {
    time = periodValue * secondsInDay;
  }
  if (period === 'weeks') {
    time = periodValue * secondsInWeek;
  }
  if (period === 'months') {
    time = periodValue * secondsInMonth;
  }
  if (period === 'years') {
    time = periodValue * secondsInYear;
  }

  return time;
}

/**
 * Returns period from seconds
 */
export function convertSecondsToPeriod(seconds: number) {
  const secondsInMinute = moment.duration(1, 'minutes').asSeconds();
  const secondsInHour = moment.duration(1, 'hours').asSeconds();
  const secondsInDay = moment.duration(1, 'days').asSeconds();
  const secondsInWeek = moment.duration(1, 'weeks').asSeconds();
  const secondsInMonth = moment.duration(1, 'months').asSeconds();
  const secondsInYear = moment.duration(1, 'years').asSeconds();

  let period = null;
  let value = 0;

  if (!period && seconds >= secondsInYear) {
    value = parseFloat((seconds / secondsInYear).toFixed(2));
    if (value % 1 === 0 || (value - 0.1) % 1 === 0) {
      period = 'years';
    }
  }

  if (!period && seconds >= secondsInMonth) {
    value = parseFloat((seconds / secondsInMonth).toFixed(2));
    if (value % 1 === 0 || (value - 0.1) % 1 === 0) {
      period = 'months';
    }
  }

  if (!period && seconds >= secondsInWeek) {
    value = parseFloat((seconds / secondsInWeek).toFixed(2));
    if (value % 1 === 0 || (value - 0.1) % 1 === 0) {
      period = 'weeks';
    }
  }

  if (!period && seconds >= secondsInDay) {
    value = parseFloat((seconds / secondsInDay).toFixed(2));
    if (value % 1 === 0 || (value - 0.1) % 1 === 0) {
      period = 'days';
    }
  }

  if (!period && seconds >= secondsInHour) {
    value = parseFloat((seconds / secondsInHour).toFixed(2));
    if (value % 1 === 0 || (value - 0.1) % 1 === 0) {
      period = 'hours';
    }
  }

  if (!period && seconds >= secondsInMinute) {
    value = parseFloat((seconds / secondsInMinute).toFixed(2));
    if (value % 1 === 0 || (value - 0.1) % 1 === 0) {
      period = 'minutes';
    }
  }

  if (value % 1 !== 0 && (value - 0.1) % 1 === 0) {
    value = Math.round(value);
  }

  if (!period && seconds === 0) {
    period = 'now';
    value = seconds;
  }

  if (!period && seconds < secondsInMinute) {
    period = 'seconds';
    value = seconds;
  }

  if (!period) {
    period = 'seconds';
    value = seconds;
  }

  return { value, period };
}

/**
 * Returns user's timezone data
 */
export function getMyTimezone() {
  let myZone = moment.tz.guess().replace(/_/g, ' ');
  const myOffset = moment.tz(myZone).format('Z');
  myZone = myZone + ' (UTC ' + myOffset + ')';
  return {
    key: -1,
    name: myZone,
    offset: myOffset,
  };
}
